/**
 * /bin-cue-to-iso orchestrator.
 *
 * Pair .cue + .bin(s) by basename, parse cue, run conversion, save outputs.
 */
(function (w) {
    'use strict';
    var DI = w.__DiskImage;
    var I18N = w.BinCueToIsoI18n || {};
    var ae = function (n, p) { try { if (typeof w.__ae === 'function') w.__ae(n, p || {}); } catch (_) {} };
    var toast = w.__toast || function (m) { console.log(m); };
    var handleErr = w.__handleError || function (e) { console.error(e); };
    var t = function (k, fb) { return I18N[k] || fb || k; };

    function _human(n) {
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
        if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
        return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
    }
    function _esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    var SLUG = 'bin-cue-to-iso';

    /**
     * Daily cap unit = ONE DISC IMAGE, not one press and not one dropped file.
     *
     * A cuesheet legitimately references 22 BINs (a per-track album rip), so
     * billing every dropped file would charge 22 against a cap labelled 5. The
     * key is the LARGEST BIN — the disc's payload, stable across re-picks — so
     * re-converting the same disc later today is free (the primitive's
     * unique-file rule), while a second, different disc costs a slot.
     */
    function _discFile(binFiles) {
        var biggest = null;
        if (binFiles) binFiles.forEach(function (f) { if (!biggest || f.size > biggest.size) biggest = f; });
        return biggest;
    }
    function _discKey(binFiles) {
        var f = _discFile(binFiles);
        if (!f || !w.__toolLimit) return null;
        var k = w.__toolLimit.fileKey(f);
        return k ? [k] : null;
    }

    function _fmt(str, vars) {
        return String(str).replace(/:([a-z_]+)/g, function (m, k) {
            return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
        });
    }

    function App(root) {
        this.root = root;
        this.cueFile = null;
        this.cueText = null;
        this.parsed = null;
        this.autoCue = null; // { layout } when the cuesheet was synthesized from a lone BIN
        this.binFiles = new Map(); // name → File
        this.lastIso = null; // File built from result.iso, kept for handoff
        this.abort = null; // AbortController of the run in flight
        this.dom = {};
    }

    App.prototype.boot = function () {
        this._cacheDom();
        this._wire();
    };

    App.prototype._cacheDom = function () {
        var r = this.root;
        this.dom.empty = r.querySelector('.bcu-empty');
        this.dom.workspace = r.querySelector('.bcu-workspace');
        this.dom.tracks = r.querySelector('.bcu-tracks');
        this.dom.btnConvert = r.querySelector('.bcu-btn-convert');
        this.dom.btnHandoff = r.querySelector('.bcu-btn-handoff');
        this.dom.btnReset = r.querySelector('.bcu-btn-reset');
        this.dom.btnCancel = r.querySelector('.bcu-btn-cancel');
        this.dom.summary = r.querySelector('.bcu-summary');
        this.dom.progress = r.querySelector('.bcu-progress');
        this.dom.progressText = r.querySelector('.bcu-progress-text');
        this.dom.progressFill = r.querySelector('.bcu-bar-fill');
        this.dom.result = r.querySelector('.bcu-result');
    };

    App.prototype._wire = function () {
        var self = this;
        var dropEl = document.getElementById('bcuDrop') || this.root.querySelector('[data-uploader-id]');
        if (dropEl) {
            // `tool-uploader:file` is the shared component's contract event
            // (fires for drop, picker AND programmatic injection). This tool
            // shipped listening to `uploader:filesready` — an event nothing
            // dispatches — with a raw input`change` fallback that always read
            // an empty FileList (the uploader clears input.value right after
            // its own dispatch). Net effect: no file could EVER enter the tool
            // (bug report 2026-07-07). Never wire the input directly.
            dropEl.addEventListener('tool-uploader:file', function (e) {
                var d = e.detail || {};
                var files = d.files || (d.file ? [d.file] : []);
                if (files.length) self.addFiles(files);
            });
        }
        if (this.dom.btnConvert) this.dom.btnConvert.addEventListener('click', function () { self.convert(); });
        if (this.dom.btnHandoff) this.dom.btnHandoff.addEventListener('click', function () { self.openInExtractor(); });
        if (this.dom.btnReset) this.dom.btnReset.addEventListener('click', function () { self.reset(); });
        if (this.dom.btnCancel) this.dom.btnCancel.addEventListener('click', function () { self.cancel(); });
    };

    App.prototype.cancel = function () {
        if (!this.abort) return;
        ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_cancel' });
        this.abort.abort();
    };

    App.prototype.openInExtractor = function () {
        if (!this.lastIso || !DI || !DI.Handoff) return;
        ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_open_in_sibling', target: 'iso-extractor' });
        DI.Handoff.send('iso-extractor', { file: this.lastIso, sourceSlug: 'bin-cue-to-iso' });
    };

    App.prototype.reset = function () {
        this.cancel();
        this.cueFile = null;
        this.cueText = null;
        this.parsed = null;
        this.autoCue = null;
        this.binFiles.clear();
        this.lastIso = null;
        this.items = null;
        // An un-settled token is simply dropped: nothing was delivered, so the
        // disc was never charged (begin() reserves, only commit() records).
        this._settle = null;
        this._hideProgress();
        if (this.dom.result) { this.dom.result.style.display = 'none'; this.dom.result.innerHTML = ''; }
        if (this.dom.empty) this.dom.empty.style.display = '';
        if (this.dom.workspace) { this.dom.workspace.style.display = 'none'; this.dom.workspace.classList.remove('active'); }
        if (this.dom.btnHandoff) this.dom.btnHandoff.style.display = 'none';
        if (this.dom.btnConvert) this.dom.btnConvert.disabled = false;
    };

    App.prototype.addFiles = function (files) {
        var self = this;
        var promises = [];
        files.forEach(function (f) {
            var name = String(f.name || '').toLowerCase();
            if (/\.cue$/.test(name)) {
                self.cueFile = f;
                self.autoCue = null;
                promises.push(f.text().then(function (text) {
                    self.cueText = text;
                    self.parsed = DI.BinCue.parseCue(text);
                }));
            } else {
                self.binFiles.set(f.name, f); // .bin/.img/.iso and anything else — treat as image data
            }
        });
        Promise.all(promises).then(function () {
            // No real cuesheet? (Re)derive a synthetic one from the lone BIN so a
            // bare .bin drop converts instead of sitting silent (bug 2026-07-07,
            // "Dragon Rage (USA).bin" — a plain MODE1/2352 dump without its cue).
            if (!self.cueFile) {
                self.cueText = null;
                self.parsed = null;
                self.autoCue = null;
                return self._autoCue();
            }
        }).then(function () {
            self._render();
            // Early wall: the answer arrives while the disc is being assembled,
            // not from a dead-feeling Convert press (the 2026-07-21 /join lesson).
            var disc = _discFile(self.binFiles);
            if (disc && w.__toolLimit && w.__toolLimit.precheck) w.__toolLimit.precheck(SLUG, [disc]);
        }).catch(function (err) {
            handleErr(err, 'bin_cue_add_files');
            ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_addfiles_error', reason: String((err && err.message) || err).slice(0, 60) });
        });
    };

    /**
     * Synthesize a single-data-track cuesheet for a lone BIN. Only when the
     * sector layout is unambiguous: the 2352/2336 fingerprint comes from the
     * 12-byte sync pattern (detectSectorLayout), and the file size must divide
     * evenly by the layout's sector size. MODE1/2048 is the detector's blind
     * fallback, so it is additionally required to carry an ISO9660 'CD001'
     * signature before we trust it. Anything ambiguous stays in the
     * "add the .cue" state rather than producing a garbage ISO.
     */
    App.prototype._autoCue = function () {
        var self = this;
        if (self.binFiles.size !== 1 || !DI || !DI.BinCue || !DI.BinaryReader) return Promise.resolve();
        var bin = self.binFiles.values().next().value;
        var reader = new DI.BinaryReader(bin);
        return DI.BinCue.detectSectorLayout(reader, 0).then(function (layout) {
            var bps = { 'MODE1/2048': 2048, 'MODE1/2352': 2352, 'MODE2/2336': 2336, 'MODE2/2352': 2352 }[layout];
            if (!bps || bin.size % bps !== 0) return;
            var confident = layout !== 'MODE1/2048'
                ? Promise.resolve(true)
                : reader.ascii(16 * 2048 + 1, 5).then(function (s) { return s === 'CD001'; }).catch(function () { return false; });
            return confident.then(function (ok) {
                if (!ok) return;
                var cueText = 'FILE "' + String(bin.name).replace(/"/g, '') + '" BINARY\n  TRACK 01 ' + layout + '\n    INDEX 01 00:00:00\n';
                self.cueText = cueText;
                self.parsed = DI.BinCue.parseCue(cueText);
                self.cueFile = null;
                self.autoCue = { layout: layout };
                ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_auto_cue', layout: layout });
            });
        }).catch(function (_) { /* tiny/unreadable file — the need-cue state renders instead */ });
    };

    App.prototype._render = function () {
        var hasCue = !!(this.cueText && this.parsed);
        if (!hasCue && !this.binFiles.size) return; // nothing loaded yet

        // `.active` matters as much as the display toggle: app.css's shared
        // workspace-preview gate ([class$=-workspace]:not(.active) > *) hides
        // every child of an inactive workspace, so without it the panel shows
        // as an empty strip. The cluster shipped without it — see 2026-07-07.
        if (this.dom.workspace) { this.dom.workspace.style.display = ''; this.dom.workspace.classList.add('active'); }

        if (!hasCue) {
            // BIN(s) received but no cuesheet and auto-detection wasn't
            // confident. Previously this state rendered NOTHING — the exact
            // "dropped the file, no reaction" silence of the 2026-07-07
            // report. Show what arrived + what's needed; keep the zone up.
            if (this.dom.empty) this.dom.empty.style.display = '';
            var names = [];
            this.binFiles.forEach(function (f) { names.push(_esc(f.name) + ' (' + _human(f.size) + ')'); });
            var htmlNeed = '<div><b>' + t('bin_received', 'Image received') + ':</b> ' + names.join(', ') + '</div>';
            htmlNeed += '<div class="bcu-warn">' + t('need_cue', 'Now add the matching .cue file — without it the track layout cannot be determined.') + '</div>';
            if (this.dom.summary) this.dom.summary.innerHTML = htmlNeed;
            if (this.dom.tracks) this.dom.tracks.innerHTML = '';
            if (this.dom.btnConvert) this.dom.btnConvert.disabled = true;
            if (this.dom.btnHandoff) this.dom.btnHandoff.style.display = 'none';
            return;
        }

        // Pair check
        var missing = [];
        (this.parsed.files || []).forEach(function (f) {
            var found = false;
            this.binFiles.forEach(function (_, k) {
                if (k.toLowerCase() === String(f.name || '').toLowerCase() ||
                    k.split(/[\\/]/).pop().toLowerCase() === String(f.name || '').split(/[\\/]/).pop().toLowerCase()) found = true;
            });
            if (!found) missing.push(f.name);
        }, this);
        // Upload zone stays visible while the set is still incomplete —
        // hiding it while the summary asks for more files (the old Missing-BIN
        // behaviour) left users with no way to add them short of Reset.
        var incomplete = missing.length > 0 || !!this.autoCue;
        if (this.dom.empty) this.dom.empty.style.display = incomplete ? '' : 'none';
        var html = '<div><b>' + t('cue_loaded', 'CUE loaded') + ':</b> ' + (this.cueFile ? _esc(this.cueFile.name) : _esc(t('auto_cue_label', 'auto-generated — single data track'))) + '</div>';
        html += '<div><b>' + t('files_referenced', 'BINs referenced') + ':</b> ' + (this.parsed.files || []).length + '</div>';
        html += '<div><b>' + t('tracks', 'Tracks') + ':</b> ' + (this.parsed.tracks || []).length + '</div>';
        if (missing.length) {
            html += '<div class="bcu-warn">' + t('missing_bin', 'Missing BIN') + ': ' + missing.map(_esc).join(', ') + '</div>';
        }
        if (this.autoCue) {
            html += '<div class="bcu-note">' + _esc(t('auto_cue_note', 'No .cue provided — assuming a single data track. If this disc has audio tracks, add its original .cue file.')) + ' (' + _esc(this.autoCue.layout) + ')</div>';
            if (this.autoCue.layout === 'MODE1/2048') {
                html += '<div class="bcu-note">' + _esc(t('already_iso_note', 'The image already uses plain 2048-byte sectors — the output ISO is an exact copy of it.')) + '</div>';
            }
        }
        if (this.dom.summary) this.dom.summary.innerHTML = html;
        if (this.dom.btnConvert) this.dom.btnConvert.disabled = missing.length > 0;
        if (this.dom.btnHandoff) this.dom.btnHandoff.style.display = 'none';

        var trHtml = '<table class="bcu-track-table"><tr><th>#</th><th>Type</th><th>Title</th><th>Performer</th><th>Pregap</th></tr>';
        (this.parsed.tracks || []).forEach(function (tr) {
            trHtml += '<tr><td>' + tr.number + '</td><td>' + _esc(tr.type) + '</td><td>' + _esc(tr.title || '') + '</td><td>' + _esc(tr.performer || '') + '</td><td>' + (tr.pregap ? (tr.pregap.sectors + ' sec') : '') + '</td></tr>';
        });
        trHtml += '</table>';
        if (this.dom.tracks) this.dom.tracks.innerHTML = trHtml;
    };

    /** Stem for the output files: the cue's name, else the first BIN's. */
    App.prototype._baseName = function () {
        if (this.cueFile) return this.cueFile.name;
        var first = this.binFiles.values().next();
        return first && first.value ? first.value.name : '';
    };

    App.prototype.convert = function () {
        var self = this;
        if (!self.cueText) { toast(t('drop_cue_first', 'Drop a .cue file first'), 'warning'); return; }
        if (!self.binFiles.size) { toast(t('drop_bin_first', 'Drop the matching .bin file'), 'warning'); return; }
        if (!DI || !DI.BinCue) { toast('BIN/CUE engine not loaded', 'error'); return; }
        if (self.abort) return; // already running
        // Daily cap. begin() is a RESERVATION: it shows the wall and returns null
        // when the day is spent, and the slot is only spent by commit() below —
        // after the files are actually handed over. Cancel, a stale handle or a
        // conversion error call abort(), so a failed run never costs a disc.
        var run = null;
        if (w.__toolLimit && w.__toolLimit.begin) {
            run = w.__toolLimit.begin(SLUG, _discKey(self.binFiles));
            if (!run) return; // note + telemetry already handled by the primitive
        }
        // The slot is spent by the FIRST artefact that actually reaches the user
        // — automatic or button-clicked. `settle` is handed to the result card
        // (see _renderResult) so a disc taken one file at a time still costs
        // exactly one image, and a disc nobody took costs nothing.
        var settle = function (ok) { if (run) { ok ? run.commit() : run.abort(); run = null; } };
        self._settle = settle;
        self.abort = (typeof AbortController === 'function') ? new AbortController() : null;
        self._runStart();
        var t0 = Date.now();
        ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_convert_start', bin_count: self.binFiles.size });

        DI.BinCue.convert(self.cueText, self.binFiles, {
            baseName: self._baseName(),
            signal: self.abort ? self.abort.signal : null,
            onProgress: function (p) { self._progress(p); }
        }).then(function (result) {
            // ── ONE automatic download. Not a style choice — a measured limit ──
            // A browser hands over exactly ONE file per user gesture. Measured in
            // real Chrome 151 (tests/playwright/… can't see this: Playwright's
            // CDP download override bypasses the gate, so it stayed green while
            // prod was broken — the probe was a real Chrome profile + a real
            // keypress, scratchpad run-chrome-probe.ps1):
            //   1 file  per press            → 4 runs, 4/4 delivered
            //   3 files per press (this tool) → file #1 delivered, #2 and #3
            //     SILENTLY dropped — no download record, no error, nothing JS
            //     can observe — AND the tab is poisoned: every later run of the
            //     same tab delivers NOTHING, even from a fresh Convert click.
            //     Only a user-initiated reload (F5) clears it.
            // That is bug report 2026-08-08 verbatim: "worked the first time and
            // after that every time I used it, it wouldn't let me download …
            // it kept saying it had been placed in my browser's download folder
            // when it was in fact not". Every mixed-mode disc also lost its WAVs
            // and EVERY disc lost its cuesheet (always queued last), which is
            // what makes the ISO alone useless to an emulator.
            // So: the ISO goes automatically, everything else waits behind a
            // button whose click is its own gesture.
            var items = [];
            if (result.iso) {
                var isoName = result.isoName || 'image.iso';
                try { self.lastIso = new File([result.iso], isoName, { type: 'application/octet-stream' }); }
                catch (_) { self.lastIso = result.iso; }
                items.push({ blob: result.iso, name: isoName, icon: '💿', kind: 'iso' });
            }
            (result.wavs || []).forEach(function (wav) {
                items.push({ blob: wav.blob, name: wav.name, icon: '🎵', kind: 'wav' });
            });
            items.push({
                blob: new Blob([result.cueText], { type: 'text/plain' }),
                name: result.cueName || 'image.cue', icon: '📄', kind: 'cue'
            });
            self.items = items;

            return self._renderResult(result, items).then(function () {
                var auto = items[0];
                auto.auto = true;
                return self._deliver(auto);
            }).then(function () {
                self._runEnd();
                ae('feature_use', {
                    slug: 'bin-cue-to-iso', action: 'bin_cue_convert_done',
                    mode: result.mode || 'unknown', wav_count: (result.wavs || []).length,
                    ms: Date.now() - t0,
                    artefacts: items.length,
                    output_iso_mb: result.iso ? Math.round(result.iso.size / 1024 / 1024) : 0
                });
                toast(items.length > 1
                    ? t('converted_take_rest', 'Converted. The rest of the files are waiting on the buttons below.')
                    : t('converted', 'Converted'), 'success');
            });
        }).catch(function (err) {
            settle(false); // nothing was delivered → the day's slot stays unspent
            self._runEnd();
            if (err && err.name === 'AbortError') {
                toast(t('cancelled', 'Conversion cancelled'), 'info');
                ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_cancelled', ms: Date.now() - t0 });
                return;
            }
            // Every conversion failure prod has ever logged for this tool (17 in
            // July 2026) was this one class: the File handle went stale mid-read
            // (NotFoundError / NotReadableError / Safari's "The I/O read operation
            // failed"). Users saw the raw DOMException sentence. Route it through
            // the shared classifier and say the one useful thing — pick it again —
            // with the upload zone back on screen.
            if (w.ToolErrors && w.ToolErrors.isFileLostError && w.ToolErrors.isFileLostError(err)) {
                toast(t('file_lost_at_select', 'Could not read the file. Please select it again.'), 'error');
                if (self.dom.empty) self.dom.empty.style.display = '';
                ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_file_lost' });
                return;
            }
            handleErr(err, 'bin_cue_convert');
            ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_convert_unsupported', reason: String(err && err.message || err).slice(0, 60) });
        });
    };

    function _save(blob, name) {
        if (typeof w.__downloadFile === 'function') return Promise.resolve(w.__downloadFile(blob, name));
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = name;
        document.body.appendChild(a); a.click();
        setTimeout(function () { try { document.body.removeChild(a); } catch (_) {} URL.revokeObjectURL(url); }, 6000);
        return Promise.resolve();
    }

    /**
     * Hand ONE artefact to the user and reflect the outcome on its row.
     *
     * The row never claims a checkmark for the classic <a download> channel:
     * that anchor returns `true` whether the browser saved the file or dropped
     * it on the floor, and a green tick for a file that never landed is the
     * very lie this whole change removes. A tick is earned only by the folder
     * path, where the write is confirmed by close() resolving.
     */
    App.prototype._deliver = function (item) {
        var self = this;
        return Promise.resolve(_save(item.blob, item.name)).then(function (ok) {
            if (ok === false) return false;      // the primitive already said why
            item.taken = true;
            self._markTaken(item);
            // The disc is charged by the first artefact that reaches the user,
            // not by the conversion finishing.
            if (self._settle) { self._settle(true); self._settle = null; }
            ae('feature_use', {
                slug: 'bin-cue-to-iso', action: 'bin_cue_artefact_taken',
                kind: item.kind, auto: item.auto ? 1 : 0, mb: Math.round(item.blob.size / 1048576)
            });
            return true;
        });
    };

    App.prototype._markTaken = function (item) {
        if (!item._btn) return;
        item._btn.textContent = t('btn_download_again', 'Download again');
        item._btn.classList.remove('lg-btn-primary');
    };

    /**
     * "Save all to folder…" — the one channel that is neither rationed nor
     * silent. showDirectoryPicker() is a single prompt for the whole set, the
     * writes are confirmed, and the user chooses where the files go (so the
     * primitive's "look in your Downloads folder" hint would be wrong here and
     * is deliberately not shown). Chromium-desktop only; everywhere else the
     * per-file buttons are the path, which is why this is an EXTRA control and
     * never the only one.
     */
    App.prototype.saveAllToFolder = function () {
        var self = this;
        if (!self.items || typeof w.showDirectoryPicker !== 'function' || !w.__FsaSink) return;
        ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_folder_pick', n: self.items.length });
        w.showDirectoryPicker({ mode: 'readwrite', id: 'timbrica-bincue', startIn: 'downloads' }).then(function (dir) {
            var sink = w.__FsaSink.create(dir);
            var written = 0;
            var chain = Promise.resolve();
            self.items.forEach(function (item) {
                chain = chain.then(function () {
                    return sink.openFile(item.name).then(function (wr) {
                        return wr.write(item.blob).then(function () { return wr.close(); });
                    }).then(function () {
                        written++;
                        item.taken = true;
                        item.confirmed = true;
                        self._markTaken(item);
                        if (item._row) item._row.classList.add('bcu-row-done');
                        if (self._settle) { self._settle(true); self._settle = null; }
                    });
                });
            });
            return chain.then(function () {
                toast(_fmt(t('saved_to_folder', 'Saved :count files to the folder you picked.'), { count: written }), 'success');
                ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_folder_saved', n: written });
            });
        }).catch(function (err) {
            var n = (err && err.name) || '';
            // Closing the picker is an informed cancel, not a failure — the
            // buttons are still right there.
            if (n === 'AbortError') { ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_folder_cancel' }); return; }
            toast(t('folder_failed', 'Could not write to that folder — use the download buttons instead.'), 'error');
            ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_folder_fail', reason: String(n || err).slice(0, 40) });
        });
    };

    /**
     * What actually came out. A 90-second run that ends with a bare "Converted"
     * toast and three files in the Downloads folder leaves the user guessing —
     * and on an audio-only disc the old build handed them a 0-byte image.iso,
     * which reads as "the tool is broken". Name every artefact, and for a data
     * track say whether the cooked ISO really carries an ISO9660 volume.
     */
    App.prototype._renderResult = function (result, items) {
        var el = this.dom.result;
        if (!el) return Promise.resolve();
        var self = this;
        el.style.display = '';
        el.innerHTML = '';

        if (!result.iso) {
            var noIso = document.createElement('div');
            noIso.className = 'bcu-note';
            noIso.textContent = t('result_no_iso', 'This disc has no data track — only audio tracks were extracted, so there is no ISO.');
            el.appendChild(noIso);
        }

        // One row per artefact, each with its OWN download button. A click is a
        // fresh user gesture, and a gesture is exactly the budget a browser
        // grants for one file — so every file in the set is reachable, however
        // many there are.
        items.forEach(function (item) {
            var row = document.createElement('div');
            row.className = 'bcu-row';
            var label = document.createElement('span');
            label.className = 'bcu-row-name';
            label.textContent = item.icon + ' ' + item.name + ' — ' + _human(item.blob.size);
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'lg-btn lg-btn-primary bcu-row-dl';
            btn.textContent = t('btn_download', 'Download');
            btn.addEventListener('click', function () { self._deliver(item); });
            row.appendChild(label);
            row.appendChild(btn);
            el.appendChild(row);
            item._btn = btn;
            item._row = row;
        });

        // Placeholder kept in DOM order so the ISO9660 verdict lands under the
        // ISO row, not below the controls (the probe resolves a tick later).
        var verify = document.createElement('div');
        el.appendChild(verify);

        // Why the buttons exist, in one sentence, where the user is looking.
        // Without it a returning user re-presses Convert (the shape prod shows:
        // one session pressed it 15 times) instead of clicking the file they
        // already have.
        if (items.length > 1) {
            var note = document.createElement('div');
            note.className = 'bcu-note bcu-delivery-note';
            note.textContent = t('delivery_note', 'Browsers hand over one file per click — use the buttons to take the rest.');
            el.appendChild(note);
        }

        if (items.length > 1 && typeof w.showDirectoryPicker === 'function' && w.__FsaSink) {
            var all = document.createElement('button');
            all.type = 'button';
            all.className = 'lg-btn bcu-save-all';
            all.textContent = t('save_all_folder', 'Save all to folder…');
            all.addEventListener('click', function () { self.saveAllToFolder(); });
            el.appendChild(all);
        }

        // Converter warnings are engine-level English (exotic sector mode, a cue
        // entry with no BIN) and there is nothing the user can do about them —
        // they go to telemetry, not into 9 locales of UI as raw English.
        if ((result.warnings || []).length) {
            ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_warnings', n: result.warnings.length, first: String(result.warnings[0]).slice(0, 60) });
        }

        if (!result.iso) return Promise.resolve();
        return _isoVolume(result.iso).then(function (vol) {
            if (vol) {
                verify.className = 'bcu-ok';
                verify.textContent = '✅ ' + _fmt(t('iso_ok', 'ISO9660 verified: volume «:label»'), { label: vol });
            } else {
                verify.className = 'bcu-note';
                verify.textContent = t('iso_no_fs', 'No ISO9660 signature in the data track (normal for Mode 2 Form 2 video discs) — the sectors were still cooked to 2048 bytes.');
            }
            ae('feature_use', { slug: 'bin-cue-to-iso', action: 'bin_cue_iso_verify', ok: vol ? 1 : 0 });
            if (self.dom.btnHandoff && DI && DI.Handoff) self.dom.btnHandoff.style.display = '';
        }).catch(function () {});
    };

    /**
     * Volume identifier of the produced ISO, or '' when there is no ISO9660
     * Primary Volume Descriptor. PVD lives at sector 16 (0x8000): type byte 1,
     * then 'CD001'; the 32-byte volume id sits at +40.
     */
    function _isoVolume(blob) {
        if (!blob || blob.size < 0x8000 + 190) return Promise.resolve('');
        return blob.slice(0x8000, 0x8000 + 190).arrayBuffer().then(function (ab) {
            var b = new Uint8Array(ab);
            var sig = String.fromCharCode(b[1], b[2], b[3], b[4], b[5]);
            if (b[0] !== 1 || sig !== 'CD001') return '';
            var id = '';
            for (var i = 40; i < 72; i++) id += String.fromCharCode(b[i]);
            return id.replace(/[\x00\s]+$/, '') || 'ISO9660';
        });
    }

    App.prototype._runStart = function () {
        if (this.dom.result) { this.dom.result.style.display = 'none'; this.dom.result.innerHTML = ''; }
        if (this.dom.btnConvert) this.dom.btnConvert.disabled = true;
        if (this.dom.btnHandoff) this.dom.btnHandoff.style.display = 'none';
        if (this.dom.btnCancel) this.dom.btnCancel.style.display = this.abort ? '' : 'none';
        if (this.dom.progress) this.dom.progress.style.display = '';
        if (this.dom.progressText) this.dom.progressText.textContent = t('converting', 'Converting…');
        if (this.dom.progressFill) this.dom.progressFill.style.width = '0%';
    };

    App.prototype._runEnd = function () {
        this.abort = null;
        this._hideProgress();
        if (this.dom.btnConvert) this.dom.btnConvert.disabled = false;
        if (this.dom.btnCancel) this.dom.btnCancel.style.display = 'none';
    };

    App.prototype._progress = function (p) {
        var pct = p.total ? Math.min(100, Math.round(p.done / p.total * 100)) : 0;
        if (this.dom.progressFill) this.dom.progressFill.style.width = pct + '%';
        if (this.dom.progressText) {
            this.dom.progressText.textContent = _fmt(t('progress', 'Converting… :pct% · :done of :total'), {
                pct: pct, done: _human(p.done), total: _human(p.total)
            });
        }
    };

    App.prototype._hideProgress = function () { if (this.dom.progress) this.dom.progress.style.display = 'none'; };

    w.__BinCueToIso = { App: App };

    document.addEventListener('DOMContentLoaded', function () {
        var root = document.querySelector('.bcu-root');
        if (!root) return;
        var app = new App(root);
        app.boot();
        w.__BinCueToIsoApp = app;
        // (No Timbrica3D.init here: that is the 3D-printing core and a no-op on
        // a normal tool — telemetry comes from telemetry-tier0.js + __ae above.)
    });
})(window);
